import { useEffect, useRef, useState } from "react";
import "@/App.css";
import "@/brochure.css";
import { RabbitStory } from "@/components/RabbitStory";
import { CoreSolutions } from "@/components/CoreSolutions";
import { TrustedBrands } from "@/components/TrustedBrands";
import { MomentsOfImpact } from "@/components/MomentsOfImpact";
import { BrochureViewer } from "@/components/BrochureViewer";
import { STATS, CORE_SOLUTIONS } from "@/data/brochure";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { 
  ChevronDown, 
  ArrowRight,
  TrendingUp,
  Shield,
  Star,
  CheckCircle,
  Sparkles,
  Linkedin,
  Menu,
  X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const sectionsRef = useRef([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    yourName: '',
    email: '',
    phone: '',
    teamSize: '',
    programInterest: '',
    startDate: '',
    additionalInfo: ''
  });

  const helloWorldApi = async () => {
    try {
      const response = await axios.get(`${API}/`);
      console.log(response.data.message);
    } catch (e) {
      console.error(e, `errored out requesting / api`);
    }
  };

  useEffect(() => {
    helloWorldApi();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("section-visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    section?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
    // Re-align once lazy images above the target have loaded and shifted layout
    [900, 1800].forEach((ms) => setTimeout(() => {
      if (section && Math.abs(section.getBoundingClientRect().top) > 80) section.scrollIntoView({ behavior: "smooth" });
    }, ms));
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Thank you! We\'ll contact you within 24 hours.');
    setFormData({ companyName: '', yourName: '', email: '', phone: '', teamSize: '', programInterest: '', startDate: '', additionalInfo: '' });
  };

  const handleEnquire = (programInterest) => {
    setFormData((prev) => ({ ...prev, programInterest }));
    scrollToSection('contact');
  };

  const teamMembers = [
    {
      name: "Farmy Yow",
      role: "Founder & Lead Facilitator",
      bio: "HRD Corp Accredited Trainer with 12+ years in corporate training. Specializes in high-impact teambuilding for Pfizer, Nestlé, AirAsia & more. Certified Fitness Coach with expertise in EQ and NLP-based learning.",
      image: "https://customer-assets.emergentagent.com/job_layout-maker-4/artifacts/quw2djx0_WhatsApp%20Image%202026-01-12%20at%2010.10.06%20AM.jpeg",
      badge: "https://customer-assets.emergentagent.com/job_layout-maker-4/artifacts/wpxr1gd1_HRDC%20Accredited%20QR%20Badge.png",
      isFounder: true
    }
  ];

  const galleryImages = [
    {
      src: "https://customer-assets.emergentagent.com/job_layout-maker-4/artifacts/m8o1zunb_44D53A37-540C-4749-81DA-AF62C8A139B3.jpg",
      alt: "Corporate training session at Dainese"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_layout-maker-4/artifacts/2d5mee67_FBF748AD-5EAA-41EB-A9C4-32180E06437B.jpg",
      alt: "Team workshop facilitation"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_layout-maker-4/artifacts/qv24telt_IMG_5379.JPG",
      alt: "Goal setting presentation"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_layout-maker-4/artifacts/klnmnqxq_665e2681-493e-480a-9a24-ab56196fe02b%20%281%29.jpg",
      alt: "Wyeth Nutrition emotional development training"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_layout-maker-4/artifacts/04pd7tda_530f097b-7d9f-4a31-8436-1b874a70ad83.jpg",
      alt: "Executive wellness workshop"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_layout-maker-4/artifacts/p7wrqfo1_Bjak%20-66.jpg",
      alt: "BJAK interactive team session"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_layout-maker-4/artifacts/xlerlpo2_IMG_2634.JPG",
      alt: "BJAK Breakthrough Experience team"
    }
  ];

  const testimonials = [
    {
      author: "Valerie Shena",
      role: "Country People Manager, Malaysia & Singapore | HRD Corp Accreditated Trainer",
      date: "August 28, 2026",
      relationship: "Farmy was Valerie's client",
      quote: "We recently engaged Atcen to facilitate a team-building event for our company, and Farmy was the lead trainer for the session. From the very beginning, it was clear that he brought a great combination of passion, professionalism, humour, and energy to the room.\n\nFarmy was highly engaging and had a natural ability to connect with the participants. He related the topics to different life and workplace situations, making the session both meaningful and easy for everyone to connect with. What I particularly appreciated was how he incorporated humour throughout the session, creating an enjoyable and relaxed atmosphere while still delivering valuable takeaways.\n\nBeyond the session itself, Farmy and the Atcen team were incredibly professional, responsive, and supportive throughout the entire process. They were also willing to go the extra mile to ensure that everything ran smoothly and that our needs were well taken care of.\n\nIf you are looking for a trainer who brings passion, humour, professionalism, and an engaging personality to the room, I would definitely recommend Farmy. He has a wonderful ability to connect with people while creating an experience that is both enjoyable and impactful."
    }
  ];

  return (
    <div className="apple-layout" data-testid="home-page">
      {/* Navigation */}
      <nav className="nav-bar" data-testid="navigation">
        <div className="nav-content">
          <div className="nav-logo" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_layout-maker-4/artifacts/tal90sht_ChatGPT_Image_Feb_23__2026__12_34_57_PM-removebg-preview.png" 
              alt="Fit Farm" 
              className="logo-mascot"
            />
            <span>Fit Farm Training & Development</span>
          </div>
          <div className="nav-links desktop-nav">
            <button onClick={() => scrollToSection('programs')} className="nav-link" data-testid="nav-programs">Programs</button>
            <button onClick={() => scrollToSection('team')} className="nav-link" data-testid="nav-team">Team</button>
            <button onClick={() => scrollToSection('gallery')} className="nav-link" data-testid="nav-gallery">Gallery</button>
            <button onClick={() => scrollToSection('testimonials')} className="nav-link" data-testid="nav-testimonials">Results</button>
            <button onClick={() => scrollToSection('profile')} className="nav-link" data-testid="nav-profile">Profile</button>
            <button onClick={() => scrollToSection('contact')} className="nav-cta" data-testid="nav-cta-button">Get Started</button>
          </div>
          <button 
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="mobile-menu" data-testid="mobile-menu">
            <button onClick={() => scrollToSection('programs')}>Programs</button>
            <button onClick={() => scrollToSection('team')}>Team</button>
            <button onClick={() => scrollToSection('gallery')}>Gallery</button>
            <button onClick={() => scrollToSection('testimonials')}>Results</button>
            <button onClick={() => scrollToSection('profile')} data-testid="mobile-nav-profile">Profile</button>
            <button onClick={() => scrollToSection('contact')} className="mobile-cta">Get Started</button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="hero-banner">
          <video 
            className="hero-video"
            autoPlay 
            loop 
            muted 
            playsInline
            poster="https://customer-assets.emergentagent.com/job_layout-maker-4/artifacts/g3r9z0wc_IMG_0036%20%281%29.JPG"
          >
            <source src="https://customer-assets-4nw71qhi.emergentagent.net/job_layout-maker-4/artifacts/r7c3ffer_video-output-6DC8C961-9027-4E62-82FD-871515198F91-2.mov" type="video/mp4" />
          </video>
          <div className="hero-overlay"></div>
          <div className="hero-content-overlay">
            <div className="hero-logo-wrapper">
              <div className="hero-logo-circle">
                <img 
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_layout-maker-4/artifacts/a7b9xuoo_images%20%2811%29.png" 
                  alt="HRD Corp Registered Training Provider"
                  className="hero-hrdc-logo"
                />
              </div>
            </div>
            <h1 className="hero-title" data-testid="hero-title">
              Corporate Training<br />
              <span className="hero-title-white">& Team Building</span>
            </h1>
            <p className="hero-subtitle" data-testid="hero-subtitle">
              Transform your workforce with our HRD Corp claimable training programs. 
              From leadership development to experiential team building, 
              we deliver impactful learning experiences that drive results.
            </p>
            <div className="hero-actions">
              <button 
                className="btn-primary" 
                onClick={() => scrollToSection('contact')}
                data-testid="hero-consult-button"
              >
                Book a Consultation
                <ArrowRight className="btn-icon" />
              </button>
              <button 
                className="btn-secondary"
                onClick={() => scrollToSection('programs')}
                data-testid="hero-programs-button"
              >
                Explore Programs
              </button>
            </div>
          </div>
        </div>
        
        {/* Stats Banner with Image */}
        <div className="stats-banner" data-testid="hero-stats">
          <img 
            src="https://customer-assets.emergentagent.com/job_layout-maker-4/artifacts/g3r9z0wc_IMG_0036%20%281%29.JPG" 
            alt="Farmy Yow presenting"
            className="stats-banner-image"
          />
          <div className="stats-banner-overlay"></div>
          <div className="stats-banner-content">
            {STATS.map((stat, i) => (
              <div key={stat.label} style={{ display: 'contents' }}>
                {i > 0 && <div className="stat-divider"></div>}
                <div className="stat-item" data-testid={`stat-${i}`}>
                  <span className="stat-number">{stat.number}</span>
                  <span className="stat-label">{stat.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button 
          className="scroll-indicator" 
          onClick={() => scrollToSection('upcoming-programs')}
          data-testid="scroll-indicator"
          aria-label="Scroll to content"
        >
          <ChevronDown className="scroll-icon" />
        </button>
      </section>

      {/* Upcoming Programs Section */}
      <section 
        id="upcoming-programs" 
        className="upcoming-section section-animate"
        ref={(el) => (sectionsRef.current[0] = el)}
        data-testid="upcoming-programs-section"
      >
        <div className="section-header">
          <h2 className="section-title" data-testid="upcoming-programs-title">Upcoming Programs</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">Stay tuned for our next upcoming programs</p>
        </div>
        
        <div className="talks-grid">
          <div className="upcoming-placeholder" data-testid="upcoming-placeholder">
            <p>New programs coming soon...</p>
          </div>
        </div>
      </section>

      {/* Recent Programs Section */}
      <section 
        id="upcoming" 
        className="upcoming-section section-animate"
        ref={(el) => (sectionsRef.current[1] = el)}
        data-testid="upcoming-section"
      >
        <div className="section-header">
          <h2 className="section-title" data-testid="upcoming-title">Recent Programs</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">Recent training events and workshops led by Farmy Yow</p>
        </div>
        
        <div className="talks-grid">
          <div className="upcoming-card" data-testid="upcoming-event-mental-resilience">
            <img 
              src="https://customer-assets-4nw71qhi.emergentagent.net/job_layout-maker-4/artifacts/t7tuhiuf_WhatsApp%20Image%202026-10-03%20at%2012.48.41%20AM.jpeg" 
              alt="Mental Resilience at Work - 22nd September 2026 - Pengerang, Johor"
              className="upcoming-image"
            />
          </div>
          
          <div className="upcoming-card" data-testid="upcoming-event-people-leaders">
            <img 
              src="https://customer-assets-4nw71qhi.emergentagent.net/job_layout-maker-4/artifacts/33m0foo9_ChatGPT%20Image%20Sep%201%2C%202026%2C%2003_34_43%20AM.png" 
              alt="People Leaders: Connecting Across Cultures & Building Stronger Teams - 5th September 2026"
              className="upcoming-image"
            />
          </div>
          
          <div className="upcoming-card" data-testid="upcoming-event-leadership">
            <img 
              src="https://customer-assets-4nw71qhi.emergentagent.net/job_layout-maker-4/artifacts/dhwx7zi9_IMG_6061.JPG" 
              alt="Leadership for All - Lead with Purpose. Empower Everyone. - 27 August 2026 - Kulim Hi-Tech"
              className="upcoming-image"
            />
          </div>
          
          <div className="upcoming-card" data-testid="upcoming-event-coaching">
            <img 
              src="https://customer-assets-4nw71qhi.emergentagent.net/job_layout-maker-4/artifacts/74qrwh5u_ChatGPT%20Image%20Aug%2017%2C%202026%2C%2012_55_03%20AM.png" 
              alt="Coaching for High Performance - From Manager to Performance Coach - 20th August 2026"
              className="upcoming-image"
            />
          </div>
          
          <div className="upcoming-card" data-testid="upcoming-event-2">
            <img 
              src="https://customer-assets-4nw71qhi.emergentagent.net/job_layout-maker-4/artifacts/q2uw2pi1_ChatGPT%20Image%20Aug%201%2C%202026%2C%2002_08_52%20AM.png" 
              alt="Emotional Resilience at Work: Managing Mental Wellness - 20 July 2026"
              className="upcoming-image"
            />
          </div>
          
          <div className="upcoming-card" data-testid="upcoming-event-1">
            <a 
              href="https://www.nationaltrainingweek.gov.my/courseDetails?courseId=29265" 
              target="_blank" 
              rel="noopener noreferrer"
              className="upcoming-link"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_layout-maker-4/artifacts/fjnozzz8_NTW_EQ_Farmy_12June2026.jpg" 
                alt="EQ: Mastering High-stakes Conversations - 12 June 2026 - National Training Week"
                className="upcoming-image"
              />
              <div className="upcoming-overlay">
                <span className="upcoming-cta">View Details</span>
              </div>
            </a>
          </div>
        </div>
      </section>

      <RabbitStory innerRef={(el) => (sectionsRef.current[8] = el)} />

      <CoreSolutions innerRef={(el) => (sectionsRef.current[2] = el)} onEnquire={handleEnquire} />

      {/* Why Us Section */}
      <section 
        id="why-us" 
        className="why-section section-animate"
        ref={(el) => (sectionsRef.current[3] = el)}
        data-testid="why-section"
      >
        <div className="why-container">
          <div className="why-image">
            <img 
              src="https://customer-assets.emergentagent.com/job_layout-maker-4/artifacts/tal90sht_ChatGPT_Image_Feb_23__2026__12_34_57_PM-removebg-preview.png" 
              alt="Fit Farm Mascot"
              data-testid="why-mascot"
            />
          </div>
          <div className="why-content">
            <h2 className="section-title left-align" data-testid="why-title">Why Choose Fit Farm Training?</h2>
            <p className="why-intro">As an HRD Corp registered training provider with over 12 years of experience, we deliver impactful corporate training and team building programs for leading organizations across Malaysia and Southeast Asia.</p>
            
            <div className="benefits-grid">
              <div className="benefit-card" data-testid="benefit-1">
                <div className="benefit-icon"><Shield /></div>
                <div className="benefit-content">
                  <h4>HRD Corp Claimable</h4>
                  <p>Our training programs are registered with HRD Corp, allowing your organization to claim training costs.</p>
                </div>
              </div>
              <div className="benefit-card" data-testid="benefit-2">
                <div className="benefit-icon"><Sparkles /></div>
                <div className="benefit-content">
                  <h4>Certified Trainers</h4>
                  <p>Accredited facilitators with expertise in leadership, EQ, NLP, and experiential learning methodologies.</p>
                </div>
              </div>
              <div className="benefit-card" data-testid="benefit-3">
                <div className="benefit-icon"><TrendingUp /></div>
                <div className="benefit-content">
                  <h4>Proven Track Record</h4>
                  <p>Trusted by Pfizer, Nestlé, AirAsia, BJAK, Bank Rakyat, Wyeth Nutrition and many more, with 10,000+ participants impacted across 6 countries.</p>
                </div>
              </div>
            </div>
            
            <div className="mission-box" data-testid="mission-box">
              <h4>Our Approach</h4>
              <p>We combine experiential learning with practical application. Our programs are designed to create lasting behavioral change through engaging activities, expert facilitation, and customized content that addresses your organization's specific challenges and goals.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section 
        id="team" 
        className="team-section section-animate"
        ref={(el) => (sectionsRef.current[4] = el)}
        data-testid="team-section"
      >
        <div className="section-header">
          <h2 className="section-title" data-testid="team-title">Your Training Facilitator</h2>
          <p className="section-subtitle">HRD Corp accredited trainer with proven expertise in corporate training</p>
        </div>

        <div className="team-grid">
          {teamMembers.map((member, index) => (
            <div key={index} className={`team-card ${member.isFounder ? 'team-card-founder' : ''}`} data-testid={`team-member-${index}`}>
              <div className="team-image-wrapper">
                <img 
                  src={member.image} 
                  alt={member.name}
                  className="team-image"
                />
              </div>
              {member.badge && (
                <div className="team-badge-wrapper">
                  <img 
                    src={member.badge} 
                    alt="HRD Corp Accredited Trainer"
                    className="team-badge"
                  />
                </div>
              )}
              <div className="team-info">
                <h3 className="team-name">{member.name}</h3>
                <span className="team-role">{member.role}</span>
                <p className="team-bio">{member.bio}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Gallery Section */}
      <section 
        id="gallery" 
        className="gallery-section section-animate"
        ref={(el) => (sectionsRef.current[5] = el)}
        data-testid="gallery-section"
      >
        <div className="section-header">
          <h2 className="section-title" data-testid="gallery-title">Our Training in Action</h2>
          <p className="section-subtitle">Photos from our corporate training and team building programs with leading organizations</p>
        </div>

        <div className="gallery-grid" data-testid="gallery-grid">
          {galleryImages.map((image, index) => (
            <div 
              key={index} 
              className={`gallery-item ${index === 0 ? 'gallery-item-large' : ''}`}
              data-testid={`gallery-item-${index}`}
            >
              <img 
                src={image.src} 
                alt={image.alt}
                className="gallery-image"
              />
              <div className="gallery-overlay">
                <span className="gallery-caption">{image.alt}</span>
              </div>
            </div>
          ))}
        </div>

        <MomentsOfImpact />
      </section>

      <TrustedBrands innerRef={(el) => (sectionsRef.current[9] = el)} />

      {/* Testimonials Section */}
      <section 
        id="testimonials" 
        className="testimonials-section section-animate"
        ref={(el) => (sectionsRef.current[6] = el)}
        data-testid="testimonials-section"
      >
        <div className="section-header">
          <h2 className="section-title" data-testid="testimonials-title">Client Testimonials</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">Verified recommendations from HR leaders and executives on LinkedIn</p>
        </div>

        <div className="testimonials-grid single-testimonial">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="linkedin-testimonial-card" data-testid={`testimonial-${index}`}>
              <div className="linkedin-testimonial-header">
                <div className="linkedin-author-info">
                  <div className="linkedin-author-avatar">
                    {testimonial.author.charAt(0)}
                  </div>
                  <div className="linkedin-author-details">
                    <div className="linkedin-author-name-row">
                      <h4 className="linkedin-author-name">{testimonial.author}</h4>
                      <div className="linkedin-verified-icon" title="LinkedIn Verified">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2L4 6v6c0 5.55 3.84 10.74 8 12 4.16-1.26 8-6.45 8-12V6l-8-4z" fill="#0A66C2"/>
                          <path d="M9.5 12.5l2 2 4-4" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span className="linkedin-connection">· 1st</span>
                    </div>
                    <p className="linkedin-author-title">{testimonial.role}</p>
                    <p className="linkedin-testimonial-date">{testimonial.date}, {testimonial.relationship}</p>
                  </div>
                </div>
                <div className="linkedin-brand-badge">
                  <Linkedin className="linkedin-icon" />
                </div>
              </div>
              
              <div className="linkedin-testimonial-body">
                {testimonial.quote.split('\n\n').map((paragraph, i) => (
                  <p key={i} className="linkedin-testimonial-paragraph">{paragraph}</p>
                ))}
              </div>
              
              <div className="linkedin-testimonial-stars">
                {[...Array(5)].map((_, i) => <Star key={i} className="star-icon" />)}
              </div>
            </div>
          ))}
        </div>
        
        <div className="linkedin-cta-wrapper">
          <a 
            href="https://www.linkedin.com/in/farmyyow/details/recommendations/?detailScreenTabIndex=0" 
            target="_blank" 
            rel="noopener noreferrer"
            className="linkedin-cta-button"
            data-testid="view-linkedin-recommendations"
          >
            <Linkedin className="linkedin-cta-icon" />
            View All Recommendations on LinkedIn
            <ArrowRight className="btn-icon" />
          </a>
        </div>
      </section>

      <BrochureViewer innerRef={(el) => (sectionsRef.current[10] = el)} />

      {/* Contact Section */}
      <section 
        id="contact" 
        className="contact-section section-animate"
        ref={(el) => (sectionsRef.current[7] = el)}
        data-testid="contact-section"
      >
        <div className="contact-container">
          <div className="contact-info">
            <h2 className="section-title left-align" data-testid="contact-title">Request a Proposal</h2>
            <p className="contact-intro">Tell us about your training needs and we'll create a customized proposal for your organization. All our programs are HRD Corp claimable.</p>
            
            <div className="contact-features">
              <div className="contact-feature">
                <CheckCircle className="feature-icon" />
                <span>Free consultation & needs assessment</span>
              </div>
              <div className="contact-feature">
                <CheckCircle className="feature-icon" />
                <span>HRD Corp claimable programs</span>
              </div>
              <div className="contact-feature">
                <CheckCircle className="feature-icon" />
                <span>Customized curriculum design</span>
              </div>
            </div>
          </div>
          
          <form className="contact-form" onSubmit={handleSubmit} data-testid="contact-form">
            <div className="form-row">
              <div className="form-group">
                <label>Company Name *</label>
                <input 
                  type="text" 
                  name="companyName" 
                  value={formData.companyName}
                  onChange={handleFormChange}
                  required 
                  data-testid="input-company"
                />
              </div>
              <div className="form-group">
                <label>Your Name *</label>
                <input 
                  type="text" 
                  name="yourName" 
                  value={formData.yourName}
                  onChange={handleFormChange}
                  required 
                  data-testid="input-name"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Email *</label>
                <input 
                  type="email" 
                  name="email" 
                  value={formData.email}
                  onChange={handleFormChange}
                  required 
                  data-testid="input-email"
                />
              </div>
              <div className="form-group">
                <label>Phone *</label>
                <input 
                  type="tel" 
                  name="phone" 
                  value={formData.phone}
                  onChange={handleFormChange}
                  required 
                  data-testid="input-phone"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Team Size *</label>
                <select 
                  name="teamSize" 
                  value={formData.teamSize}
                  onChange={handleFormChange}
                  required
                  data-testid="select-team-size"
                >
                  <option value="">Select team size</option>
                  <option value="5-10">5-10 employees</option>
                  <option value="11-25">11-25 employees</option>
                  <option value="26-50">26-50 employees</option>
                  <option value="51-100">51-100 employees</option>
                  <option value="100+">100+ employees</option>
                </select>
              </div>
              <div className="form-group">
                <label>Program Interest *</label>
                <select 
                  name="programInterest" 
                  value={formData.programInterest}
                  onChange={handleFormChange}
                  required
                  data-testid="select-program"
                >
                  <option value="">Select program</option>
                  {CORE_SOLUTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.title}</option>
                  ))}
                  <option value="consultation">Not sure / Need consultation</option>
                </select>
              </div>
            </div>
            <div className="form-group full-width">
              <label>Training Requirements</label>
              <textarea 
                name="additionalInfo" 
                value={formData.additionalInfo}
                onChange={handleFormChange}
                rows="4"
                placeholder="Tell us about your training objectives, number of participants, preferred dates, and any specific requirements..."
                data-testid="textarea-info"
              ></textarea>
            </div>
            <button type="submit" className="btn-primary submit-btn" data-testid="submit-button">
              Request Proposal
              <ArrowRight className="btn-icon" />
            </button>
            <p className="form-note">We'll review your requirements and send a customized proposal within 48 hours.</p>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <img 
              src="https://customer-assets.emergentagent.com/job_layout-maker-4/artifacts/tal90sht_ChatGPT_Image_Feb_23__2026__12_34_57_PM-removebg-preview.png" 
              alt="Fit Farm" 
              className="footer-mascot"
            />
            <span>Fit Farm Training</span>
          </div>
          <div className="footer-links">
            <button onClick={() => scrollToSection('programs')}>Programs</button>
            <button onClick={() => scrollToSection('team')}>Team</button>
            <button onClick={() => scrollToSection('testimonials')}>Results</button>
            <button onClick={() => scrollToSection('profile')}>Profile</button>
            <button onClick={() => scrollToSection('contact')}>Contact</button>
          </div>
          <p className="footer-copy">© 2026 Fit Farm Training. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
